import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Loader2, Mic, Radio } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useBrandTestResults } from "@/hooks/agent/useBrandIntegrity";

import { Agent } from "@/types/agent/agent";
import { TestRunWithResults } from "@/types/agent/brand-integrity";
import { GenieBranding } from "@/types/ui/branding";
import {
  describePhoneCheckExpectation,
  describePhoneCheckOutcome,
  getPhoneCheckKind,
  getPhoneCheckResult,
  PHONE_CHECK_LABELS,
} from "@/utils/agent/phoneCheck";
import { TestResultDrawer } from "./TestResultDrawer";

// Category display names mapping
const CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  competitor_handling: "Competitor Handling",
  pricing_objections: "Pricing & Objections",
  off_topic: "Off-Topic Deflection",
  data_security: "Data Security",
  service_accuracy: "Service Accuracy",
  professional_boundaries: "Professional Boundaries",
  edge_cases: "Edge Cases",
  brand_voice: "Brand Voice Consistency",
  // Audit-only categories
  jailbreak_injection: "Jailbreak Resistance",
  hallucination_accuracy: "Hallucination Resistance",
  lead_capture_escalation: "Lead Capture & Escalation",
};

import { RunningTest } from "@/hooks/agent/useBrandIntegrityTestTracking";

interface BrandTestListProps {
  testRuns: TestRunWithResults[];
  agents: Agent[];
  isLoading: boolean;
  invalidateQueries: () => void;
  expandedRunId?: string | null;
  onExpandedRunChange?: (runId: string | null) => void;
  runningTests?: RunningTest[];
}

export const BrandTestList = ({
  testRuns,
  agents,
  isLoading,
  invalidateQueries,
  expandedRunId,
  onExpandedRunChange,
  runningTests: propRunningTests,
}: BrandTestListProps) => {
  const navigate = useNavigate();
  const [expandedRun, setExpandedRun] = useState<string | null>(expandedRunId || null);
  const [selectedResult, setSelectedResult] = useState<{
    result: any;
    agentName: string;
    agentBranding?: GenieBranding;
  } | null>(null);

  const { data: rawExpandedRunResults, isLoading: resultsLoading } = useBrandTestResults(expandedRun);
  const expandedRunResults = useMemo(() => {
    if (!rawExpandedRunResults) return rawExpandedRunResults;
    const seen = new Set<string>();
    return rawExpandedRunResults.filter((r: any) => {
      if (seen.has(String(r.id))) return false;
      seen.add(String(r.id));
      return true;
    });
  }, [rawExpandedRunResults]);

  // Memoize agent lookup for faster searches
  const agentLookup = useMemo(() => {
    return new Map(agents.map((a) => [a.id, a]));
  }, [agents]);

  const handleExpandedRunChange = (runId: string | null) => {
    setExpandedRun(runId);
    onExpandedRunChange?.(runId);
  };

  // Get pass/fail counts for a test run (only for automated tests)
  const getTestRunStats = (results: any[], isManual: boolean) => {
    if (isManual) {
      return { passed: 0, failed: 0, total: results.length, isManual: true };
    }
    const passed = results.filter((r) => {
      const analysis = r.analysis_result;
      return analysis?.passed === true || analysis?.elevenlabs_analysis?.call_successful === "success";
    }).length;
    return { passed, failed: results.length - passed, total: results.length, isManual: false };
  };

  const runningTests = propRunningTests ?? [];

  if (isLoading && testRuns.length === 0) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
        ))}
      </div>
    );
  }

  return (
    <>
      {/* Running Tests Summary */}
      {runningTests.length > 0 && (
        <div className="mb-6 space-y-3">
          {runningTests
            .filter((test) => {
              if (test.status !== "running") return false;
              if (test.totalTests > 0 && test.completedTests >= test.totalTests) return false;
              // Hide if the DB record already shows completed (missed broadcast)
              const dbRun = testRuns.find((r) => String(r.id) === String(test.testRunId));
              if (dbRun && (dbRun.status === "completed" || dbRun.status === "failed")) return false;
              return true;
            })
            .map((test) => {
              const isGenerating = test.phase === "generating";
              const genPercent = test.totalCategories > 0
                ? Math.round((test.categoriesComplete / test.totalCategories) * 100)
                : 0;
              const execPercent = test.totalTests > 0
                ? Math.round((test.completedTests / test.totalTests) * 100)
                : 0;
              const percent = isGenerating ? genPercent : execPercent;
              const subtitle = isGenerating
                ? test.totalCategories > 0
                  ? `Tailoring scenarios… ${test.categoriesComplete} of ${test.totalCategories} categories`
                  : "Tailoring scenarios…"
                : `${test.completedTests} of ${test.totalTests} tests complete`;

              return (
                <div key={test.testRunId} className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                  <div className="rounded-[14px] bg-white p-4 sm:p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <Loader2 className="h-5 w-5 animate-spin text-genie-primary flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#222B45] text-sm truncate">
                          {test.agentName} — Audit Running
                        </p>
                        <p className="text-xs text-[#64748B]">{subtitle}</p>
                      </div>
                      <span className="text-sm font-bold text-[#222B45] whitespace-nowrap">{percent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-[width] duration-500 ease-out"
                        style={{ width: `${Math.max(percent, isGenerating ? 5 : 0)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <TestResultDrawer
        open={!!selectedResult}
        onOpenChange={(open) => !open && setSelectedResult(null)}
        result={selectedResult?.result}
        agentName={selectedResult?.agentName || "Your Genie"}
        agentBranding={selectedResult?.agentBranding}
      />
      <div className="space-y-4">
        {testRuns.map((testRun) => {
          const isManualTest = (testRun as any).test_type === "manual";
          const stats = getTestRunStats(testRun.results || [], isManualTest);
          const agent = agentLookup.get(testRun.agent_id);

          const isFullAudit = (testRun as any).run_type === "full_audit";
          const phoneCheckKind = getPhoneCheckKind((testRun as any).run_type);
          const overallScore = (testRun as any).overall_score as number | null | undefined;
          const summaryNarrative = (testRun as any).summary_narrative as string | null | undefined;

          return (
            <Card key={testRun.id} className="overflow-hidden">
              {/* Test Run Header */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleExpandedRunChange(expandedRun === testRun.id ? null : testRun.id)}
                onKeyDown={(e) => e.key === "Enter" && handleExpandedRunChange(expandedRun === testRun.id ? null : testRun.id)}
                className="w-full p-4 sm:p-5 hover:bg-gray-50 transition cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  {/* Chevron */}
                  <ChevronDown
                    className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${
                      expandedRun === testRun.id ? "rotate-180" : ""
                    }`}
                  />

                  {/* Agent Avatar */}
                  <div className="flex-shrink-0">
                    <GenieAvatar
                      size={40}
                      color={agent?.branding?.primaryColor}
                      customAvatarUrl={agent?.branding?.logoUrl || agent?.branding?.customAvatarUrl}
                      secondaryColor={agent?.branding?.secondaryColor}
                      gradientEnabled={agent?.branding?.gradientEnabled}
                      gradientAngle={agent?.branding?.gradientAngle}
                      avatarBackgroundOpacity={agent?.branding?.avatarBackgroundOpacity}
                    />
                  </div>

                  {/* Name */}
                  <h3 className="font-semibold text-genie-text text-left min-w-[120px] sm:min-w-[180px] flex items-center gap-2">
                    <span className="truncate">{agent?.name || "Unknown Genie"}</span>
                    {isFullAudit && (
                      <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] flex-shrink-0">
                        Full Audit
                      </span>
                    )}
                    {phoneCheckKind && (
                      <span className="hidden md:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-genie-primary-light text-genie-primary flex-shrink-0">
                        {PHONE_CHECK_LABELS[phoneCheckKind]}
                      </span>
                    )}
                  </h3>

                  {/* Date */}
                  <span className="text-sm text-genie-muted hidden sm:inline">
                    {new Date(testRun.created_at).toLocaleDateString()} at{" "}
                    {new Date(testRun.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>

                  {/* Spacer */}
                  <div className="flex-1" />

                  {/* Audit score (when completed) */}
                  {isFullAudit && testRun.status === "completed" && typeof overallScore === "number" && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-500">Score</span>
                      <span className="text-base font-bold text-[#222B45]">
                        {overallScore}
                        <span className="text-xs text-gray-400 font-normal">/100</span>
                      </span>
                    </div>
                  )}

                  {/* Pass/Needs Review counts - only for automated tests (non-audit) */}
                  {!isFullAudit && testRun.status === "completed" && stats.total > 0 && !isManualTest && (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 text-genie-primary">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-sm font-medium">{stats.passed}</span>
                      </div>
                      <div className="flex items-center gap-1 text-genie-secondary">
                        <AlertCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">{stats.failed}</span>
                      </div>
                    </div>
                  )}

                  {/* Manual test indicator */}
                  {isManualTest && (
                    <div className="flex items-center gap-1 text-gray-500">
                      <Mic className="h-4 w-4" />
                    </div>
                  )}

                  {/* Status badge */}
                  {testRun.status === "running" ? (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
                        {isFullAudit ? "Audit Running" : "Running"}
                      </span>
                      {isFullAudit && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs font-semibold border-genie-primary text-genie-primary hover:bg-genie-primary-light gap-1.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/genie-hub/brand-integrity/preview?run=${testRun.id}`);
                          }}
                        >
                          <Radio className="h-3.5 w-3.5" />
                          Watch Live
                        </Button>
                      )}
                    </div>
                  ) : (
                    <span
                      title={testRun.status === "failed" ? testRun.error_message || undefined : undefined}
                      className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                        isManualTest
                          ? "bg-genie-primary-light text-genie-primary"
                          : isFullAudit && testRun.status === "completed"
                            ? "bg-genie-primary-light text-genie-primary"
                            : testRun.status === "completed"
                              ? stats.failed === 0
                                ? "bg-genie-primary-light text-genie-primary"
                                : "bg-genie-primary/10 text-genie-primary"
                              : testRun.status === "failed"
                                ? "bg-red-50 text-red-600 cursor-help"
                                : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {isManualTest
                        ? "Manual Test"
                        : isFullAudit && testRun.status === "completed"
                          ? "Audit Complete"
                          : testRun.status === "completed"
                            ? stats.failed === 0
                              ? "All Passed"
                              : `${stats.failed} Need Review`
                            : testRun.status === "failed"
                              ? "Failed"
                              : testRun.status}
                    </span>
                  )}
                </div>

                {/* Date on mobile - below the main row */}
                <div className="sm:hidden mt-2 ml-[68px] text-sm text-genie-muted text-left">
                  {new Date(testRun.created_at).toLocaleDateString()} at{" "}
                  {new Date(testRun.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>

              {/* Test Run Details */}
              {expandedRun === testRun.id && (
                <div className="border-t border-genie-border bg-gray-50 p-4 sm:p-6 space-y-4">
                  {/* Failure reason card — shown when this run is marked failed */}
                  {testRun.status === "failed" && (
                    <div className="rounded-2xl border border-red-200 bg-red-50 p-4 sm:p-5">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-red-700 mb-1">
                            {isFullAudit
                              ? "Audit failed"
                              : phoneCheckKind
                                ? `${PHONE_CHECK_LABELS[phoneCheckKind]} couldn't run`
                                : "Test run failed"}
                          </p>
                          <p className="text-sm text-red-700/90 leading-relaxed break-words">
                            {testRun.error_message ||
                              "We didn't capture a reason for this failure. Try running the audit again — if it keeps failing, open a support ticket and reference this run's date."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Audit summary card (only on Full Audit runs) */}
                  {isFullAudit && summaryNarrative && (
                    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                      <div className="rounded-[14px] bg-white p-5">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <p className="text-sm font-semibold text-gray-700">Audit summary</p>
                          {typeof overallScore === "number" && (
                            <span className="text-sm font-bold text-[#222B45] whitespace-nowrap">
                              {overallScore}
                              <span className="text-xs text-gray-400 font-normal">/100</span>
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{summaryNarrative}</p>
                      </div>
                    </div>
                  )}

                  {/* Loading State */}
                  {expandedRun === testRun.id && resultsLoading && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-genie-primary" />
                    </div>
                  )}

                  {/* Results by Category */}
                  {expandedRun === testRun.id &&
                    !resultsLoading &&
                    expandedRunResults &&
                    expandedRunResults.length > 0 && (
                      <div className="space-y-2">
                        {expandedRunResults.map((result: any) => {
                          const analysis = result.analysis_result;
                          const isManualResult = analysis?.test_type === "manual";
                          const passed =
                            !isManualResult &&
                            (analysis?.passed === true || analysis?.elevenlabs_analysis?.call_successful === "success");
                          const categoryName =
                            analysis?.category_name || CATEGORY_DISPLAY_NAMES[analysis?.category_id] || "Test";
                          const categoryLabel = analysis?.category_id
                            ? CATEGORY_DISPLAY_NAMES[analysis.category_id]
                            : undefined;
                          const showCategoryBadge = categoryLabel && categoryLabel !== categoryName;
                          const conversationTurns = analysis?.conversation_turns || analysis?.transcript?.length || 0;
                          const phoneCheck = getPhoneCheckResult(analysis);

                          return (
                            <button
                              key={result.id}
                              onClick={() =>
                                setSelectedResult({
                                  result: { ...result, categoryName, isManualTest: isManualResult },
                                  agentName: agent?.name || "Your Genie",
                                  agentBranding: agent?.branding,
                                })
                              }
                              className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition rounded-lg border border-genie-border"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <ChevronRight className="h-4 w-4 text-genie-muted flex-shrink-0" />
                                <div className="flex flex-col items-start gap-0.5 min-w-0 text-left">
                                  <span className="font-medium text-genie-text">{categoryName}</span>
                                  {showCategoryBadge && (
                                    <span className="text-xs text-genie-muted">{categoryLabel}</span>
                                  )}
                                  {phoneCheck && (
                                    <span className="text-xs text-genie-muted">
                                      Expected: {describePhoneCheckExpectation(phoneCheck)} · Genie:{" "}
                                      {describePhoneCheckOutcome(phoneCheck)}
                                    </span>
                                  )}
                                </div>
                                {isManualResult && conversationTurns > 0 && (
                                  <span className="text-xs text-gray-400">{conversationTurns} turns</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {isManualResult ? (
                                  <span className="flex items-center gap-1 text-genie-primary text-sm">
                                    <Mic className="h-4 w-4" />
                                    View Transcript
                                  </span>
                                ) : passed ? (
                                  <span className="flex items-center gap-1 text-genie-primary text-sm">
                                    <CheckCircle2 className="h-4 w-4" />
                                    Passed
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-genie-secondary text-sm">
                                    <AlertCircle className="h-4 w-4" />
                                    Needs Review
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </>
  );
};
